import { useEffect, useMemo, useState } from 'react';
import { useAppState } from 'src/lib/hooks';
import {
  Button,
  Card,
  Col,
  Icon,
  Link,
  Row,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  toast,
  useApp
} from 'straightline-ui';
import {
  NOTIFICATION_SOURCE,
  NOTIFICATION_TYPE
} from 'straightline-utils/constants';

export function ShipmentNotifications({ shipment }) {
  const app = useApp();
  const {
    state: { user }
  } = useAppState();

  const role = user.roles.find((role) => {
    return role.id === app.auth.payload.role_id;
  });

  const notificationSettings = role.notifications.find((notification) => {
    return notification.type === NOTIFICATION_TYPE.SHIPMENT;
  });

  const allNotifications = notificationSettings?.source.includes(
    NOTIFICATION_SOURCE.ALL
  );

  const assignedNotifications = notificationSettings?.source.includes(
    NOTIFICATION_SOURCE.ASSIGNED
  );

  const followingNotifications = notificationSettings?.source.includes(
    NOTIFICATION_SOURCE.FOLLOWING
  );

  if (!notificationSettings) {
    return (
      <InfoItem icon="bell-off" title="Notifications Disabled">
        <div className="">
          Notifications have been disabled for all shipments. Don't miss out on
          timely updates for status changes, ETA's, and more. Visit the
          <Link to="/settings/profile">Settings</Link> page to manage your
          preferences.
        </div>
      </InfoItem>
    );
  }

  if (allNotifications) {
    return (
      <InfoItem icon="bell" title="Notifications Enabled">
        <div className="">
          Notifications have been enabled for all shipments. You will receive
          alerts for status changes, ETA's, etc. Visit the{' '}
          <Link to="/settings/profile">Settings</Link> page to manage your
          preferences.
        </div>
      </InfoItem>
    );
  }

  if (assignedNotifications && shipment.shipper_user_id === user.id) {
    return (
      <InfoItem icon="bell" title="Notifications Enabled">
        <div className="">
          You are receiving notifications for this shipment because it is
          assigned to you. Visit the{' '}
          <Link to="/settings/profile">Settings</Link> page to manage your
          preferences.
        </div>
      </InfoItem>
    );
  }

  if (followingNotifications) {
    return (
      <FollowItem
        service="api/shipment-users"
        role={role}
        data={{ shipment_id: shipment.id }}
        followText="You are receiving notifications for this shipment because you are following it. Disable notifications to mute future alerts. Re-enable
        them anytime."
        unfollowText="Follow this shipment to enable timely notifications for status changes, messages, and more. You can disable notifications
        anytime."
      />
    );
  }

  return (
    <InfoItem icon="bell-off" title="Notifications Disabled">
      <div className="">
        You are not receiving notifications for this shipments because it is not
        assigned to you. Enable following shipments by visiting the{' '}
        <Link to="/settings/profile">Settings</Link>
        page.
      </div>
    </InfoItem>
  );
}

export function ShipmentQuoteNotifications({ quote }) {
  const app = useApp();

  const {
    state: { user }
  } = useAppState();

  const role = user.roles.find((role) => {
    return role.id === app.auth.payload.role_id;
  });

  const notificationSettings = role.notifications.find((notification) => {
    return notification.type === NOTIFICATION_TYPE.SHIPMENT_QUOTE;
  });

  const allNotifications = notificationSettings?.source.includes(
    NOTIFICATION_SOURCE.ALL
  );

  const assignedNotifications = notificationSettings?.source.includes(
    NOTIFICATION_SOURCE.ASSIGNED
  );

  const followingNotifications = notificationSettings?.source.includes(
    NOTIFICATION_SOURCE.FOLLOWING
  );

  if (!notificationSettings) {
    return (
      <InfoItem icon="bell-off" title="Notifications Disabled">
        <div className="">
          Notifications have been disabled for all quotes. Don't miss out on
          alerts when your offers have been accepted or denied. Visit the{' '}
          <Link to="/settings/profile">Settings</Link> page to manage your
          preferences.
        </div>
      </InfoItem>
    );
  }

  if (allNotifications) {
    return (
      <InfoItem icon="bell" title="Notifications Enabled">
        <div className="">
          Notifications have been enabled for all quotes. You will receive
          alerts about all of your price offers. Visit the{' '}
          <Link to="/settings/profile">Settings</Link> page to manage your
          preferences.
        </div>
      </InfoItem>
    );
  }

  if (assignedNotifications && quote.shipper_user_id === user.id) {
    return (
      <InfoItem icon="bell" title="Notifications Enabled">
        <div className="">
          You are receiving notifications for this quote because it is assigned
          to you. Visit the <Link to="/settings/profile">Settings</Link> page to
          manage your preferences.
        </div>
      </InfoItem>
    );
  }

  if (followingNotifications) {
    return (
      <FollowItem
        service="api/shipment-quote-users"
        role={role}
        data={{ shipment_quote_id: quote.id }}
        followText="You are receiving notifications for this quote because you are following it. Disable notifications to mute future alerts. Re-enable them anytime."
        unfollowText="Follow this quote to receive notifications and updates about price offers. You can disable notifications anytime."
      />
    );
  }

  return (
    <InfoItem icon="bell-off" title="Notifications Disabled">
      <div className="">
        You are not receiving notifications for this quote because it is not
        assigned to you. Enable following quotes by visiting the{' '}
        <Link to="/settings/profile">Settings</Link>
        page.
      </div>
    </InfoItem>
  );
}

function InfoItem({ icon, title, children }) {
  return (
    <div>
      <Row className="justify-content-between align-items-center mb-3">
        <Col>
          <div className="h3 mb-0 d-flex align-items-center">
            <Icon type={icon} className="mr-3" />
            {title}
          </div>
        </Col>
        <Col xs="auto">
          <Link type="button" color="link" to="/settings/profile">
            View Settings
          </Link>
        </Col>
      </Row>
      {children}
    </div>
  );
}

function FollowItem({ service, role, data, followText, unfollowText }) {
  const app = useApp();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const { result, error, isError, isLoading, isFetching } = app
    .service(service)
    .useFindOne({
      query: {
        ...data,
        user_role_id: role.id
      }
    });

  useEffect(() => {
    setIsChecked(!!result);
  }, [result]);

  const inputId = useMemo(() => {
    const number = new Date().getMilliseconds() * Math.random();
    return `follow-${number}`;
  }, []);

  const isDisabled = isLoading || isFetching || isUpdating;

  const handleFollow = () => {
    setIsUpdating(true);
    setIsChecked(true);
    app
      .service(service)
      .create(data)
      .then(() => {
        app.service(service).invalidateFind();
        toast.success('Subscribed to notifications!');
      })
      .catch((error) => {
        toast.errors(error);
        setIsChecked(false);
      })
      .finally(() => setIsUpdating(false));
  };

  const handleUnFollow = (shipmentUserId) => {
    setIsUpdating(true);
    setIsChecked(false);
    app
      .service(service)
      .remove(shipmentUserId)
      .then(() => {
        app.service(service).invalidateFind();
        toast.success('Muted notifications!');
      })
      .catch((error) => {
        toast.errors(error);
        setIsChecked(true);
      })
      .finally(() => setIsUpdating(false));
  };

  if (isLoading) {
    return (
      <InfoItem icon="bell" title="Notifications Enabled">
        <div>
          <div
            className="bg-light rounded text-lighter mb-2"
            style={{ width: '95%', height: '20px' }}
          ></div>
          <div
            className="bg-light rounded text-lighter"
            style={{ width: '60%', height: '19px' }}
          ></div>
        </div>
      </InfoItem>
    );
  }

  if (isError) {
    return (
      <InfoItem icon="bell-off" title="Notifications Disabled">
        <div className="text-danger">
          {error.message || 'There was an error loading notification settings'}
        </div>
      </InfoItem>
    );
  }

  return (
    <div>
      <Row className="justify-content-between align-items-center mb-3">
        <Col>
          <div className="h3 mb-0 d-flex align-items-center">
            <Icon type={result ? 'bell' : 'bell-off'} className="mr-3" />
            Notifications {result ? 'Enabled' : 'Disabled'}
          </div>
        </Col>
        <Col xs="auto">
          <div className="custom-control custom-switch">
            <input
              type="checkbox"
              className="custom-control-input"
              checked={isChecked}
              id={inputId}
              disabled={isUpdating}
              onChange={() => {
                if (isDisabled) {
                  return;
                }
                if (result) {
                  return handleUnFollow(result.id);
                }
                handleFollow();
              }}
            />
            <label className="custom-control-label" htmlFor={inputId} />
          </div>
        </Col>
      </Row>
      <div>{result ? <div>{followText}</div> : <div>{unfollowText}</div>}</div>
    </div>
  );
}

export function NotificationsPicker() {
  const app = useApp();
  const {
    state: { user },
    setInState
  } = useAppState();

  const role = user.roles.find((role) => {
    return role.id === app.auth.payload.role_id;
  });

  const notifications = role?.notifications;
  const [values, setValues] = useState(notifications);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    setValues(notifications);
  }, [notifications]);

  if (!role) {
    return (
      <Card>
        <div className="card-header h4 mb-0">
          <div className="font-weight-bold">Notifications</div>
        </div>
        <div className="p-3 text-danger">
          There was an error loading your notification settings. Please log out
          and log back in to resolve the issue.
        </div>
      </Card>
    );
  }

  const handleUpdate = (notifications) => {
    setValues(notifications);
    setIsUpdating(true);
    app
      .service('api/user')
      .create({ notifications })
      .then((updatedUser) => {
        toast.success('Notifications Updated');
        setInState('user', updatedUser);
      })
      .catch((error) => {
        toast.errors(error);
        setValues(values);
      })
      .finally(() => {
        setIsUpdating(false);
      });
  };

  const handleChange = (type, source) => {
    const current = values.find((notification) => {
      return notification.type === type;
    });
    const validValues = values.filter((notification) => {
      return notification.source?.length > 0;
    });
    if (!current) {
      const newNotifs = [...validValues, { type, source: [source] }];
      return handleUpdate(newNotifs);
    }
    if (current.source.includes(source)) {
      const newNotifs = validValues.map((notification) => {
        if (notification.type !== type) {
          return notification;
        }
        return {
          ...notification,
          source: notification.source.filter(
            (src) => src !== source && src !== NOTIFICATION_SOURCE.ALL
          )
        };
      });
      return handleUpdate(newNotifs);
    }
    const newNotifs = validValues.map((notification) => {
      if (notification.type !== type) {
        return notification;
      }
      const sources = notification.source.filter(
        (src) => src !== source && src !== NOTIFICATION_SOURCE.ALL
      );
      return {
        ...notification,
        source: [...sources, source]
      };
    });
    return handleUpdate(newNotifs);
  };

  const handleAll = (type) => {
    const current = values.find((notification) => {
      return notification.type === type;
    });
    const validValues = values.filter((notification) => {
      return notification.source?.length > 0;
    });
    if (!current) {
      const newNotifs = [
        ...validValues,
        { type, source: [NOTIFICATION_SOURCE.ALL] }
      ];
      return handleUpdate(newNotifs);
    }
    const hasAll = current.source.includes(NOTIFICATION_SOURCE.ALL);
    if (hasAll) {
      const newNotifs = validValues.filter((notification) => {
        return notification.type !== type;
      });
      return handleUpdate(newNotifs);
    }
    const newNotifs = validValues.map((notification) => {
      if (notification.type !== type) {
        return notification;
      }
      return {
        ...notification,
        source: [NOTIFICATION_SOURCE.ALL]
      };
    });
    return handleUpdate(newNotifs);
  };

  const handleDisable = () => {
    if (!values.length) {
      return;
    }
    return handleUpdate([]);
  };

  const smallStyle = {
    fontSize: '0.65rem'
  };

  return (
    <Card>
      <div className="card-header h4 mb-0">
        <div className="font-weight-bold">Notifications</div>
        <div>
          <Button
            color="white"
            size="sm"
            onClick={handleDisable}
            disabled={isUpdating || !values.length}
          >
            Disable All
          </Button>
        </div>
      </div>
      <Table className="table mb-0">
        <TableHead>
          <TableRow>
            <TableHeader className="" style={smallStyle}>
              Type
            </TableHeader>
            <TableHeader className="text-center" style={smallStyle}>
              All
            </TableHeader>
            <TableHeader className="text-center" style={smallStyle}>
              Owned
            </TableHeader>
            <TableHeader className="text-center" style={smallStyle}>
              Following
            </TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableData className="">
              <div className="font-weight-bold">Shipments</div>
              <div className="small">
                Follow your shipments from "Booked" to "Delivered" and see every
                detail and alert along the way.
              </div>
            </TableData>
            <TableData className="text-center">
              <div className="custom-control custom-checkbox mr-n2">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="shipments-all"
                  checked={
                    !!values.find((notification) => {
                      return (
                        notification.type === NOTIFICATION_TYPE.SHIPMENT &&
                        notification.source.includes(NOTIFICATION_SOURCE.ALL)
                      );
                    })
                  }
                  disabled={isUpdating}
                  onChange={() => handleAll(NOTIFICATION_TYPE.SHIPMENT)}
                />
                <label
                  className="custom-control-label"
                  htmlFor="shipments-all"
                />
              </div>
            </TableData>
            <TableData className="text-center">
              <div className="custom-control custom-checkbox mr-n2">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="shipments-assigned"
                  checked={
                    !!values.find((notification) => {
                      return (
                        notification.type === NOTIFICATION_TYPE.SHIPMENT &&
                        notification.source.includes(
                          NOTIFICATION_SOURCE.ASSIGNED
                        )
                      );
                    })
                  }
                  disabled={isUpdating}
                  onChange={() =>
                    handleChange(
                      NOTIFICATION_TYPE.SHIPMENT,
                      NOTIFICATION_SOURCE.ASSIGNED
                    )
                  }
                />
                <label
                  className="custom-control-label"
                  htmlFor="shipments-assigned"
                />
              </div>
            </TableData>
            <TableData className="text-center">
              <div className="custom-control custom-checkbox mr-n2">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="shipments-following"
                  checked={
                    !!values.find((notification) => {
                      return (
                        notification.type === NOTIFICATION_TYPE.SHIPMENT &&
                        notification.source.includes(
                          NOTIFICATION_SOURCE.FOLLOWING
                        )
                      );
                    })
                  }
                  disabled={isUpdating}
                  onChange={() =>
                    handleChange(
                      NOTIFICATION_TYPE.SHIPMENT,
                      NOTIFICATION_SOURCE.FOLLOWING
                    )
                  }
                />
                <label
                  className="custom-control-label"
                  htmlFor="shipments-following"
                />
              </div>
            </TableData>
          </TableRow>
          <TableRow>
            <TableData className="">
              <div className="font-weight-bold">Quotes</div>
              <div className="small">
                Stay on top of your negotiation process by getting notifications
                when your offers are accepted or denied.
              </div>
            </TableData>
            <TableData className="text-center">
              <div className="custom-control custom-checkbox mr-n2">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="shipment-quotes-all"
                  checked={
                    !!values.find((notification) => {
                      return (
                        notification.type ===
                          NOTIFICATION_TYPE.SHIPMENT_QUOTE &&
                        notification.source.includes(NOTIFICATION_SOURCE.ALL)
                      );
                    })
                  }
                  disabled={isUpdating}
                  onChange={() => handleAll(NOTIFICATION_TYPE.SHIPMENT_QUOTE)}
                />
                <label
                  className="custom-control-label"
                  htmlFor="shipment-quotes-all"
                />
              </div>
            </TableData>
            <TableData className="text-center">
              <div className="custom-control custom-checkbox mr-n2">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="shipment-quotes-assigned"
                  checked={
                    !!values.find((notification) => {
                      return (
                        notification.type ===
                          NOTIFICATION_TYPE.SHIPMENT_QUOTE &&
                        notification.source.includes(
                          NOTIFICATION_SOURCE.ASSIGNED
                        )
                      );
                    })
                  }
                  disabled={isUpdating}
                  onChange={() =>
                    handleChange(
                      NOTIFICATION_TYPE.SHIPMENT_QUOTE,
                      NOTIFICATION_SOURCE.ASSIGNED
                    )
                  }
                />
                <label
                  className="custom-control-label"
                  htmlFor="shipment-quotes-assigned"
                />
              </div>
            </TableData>
            <TableData className="text-center">
              <div className="custom-control custom-checkbox mr-n2">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="shipment-quotes-following"
                  checked={
                    !!values.find((notification) => {
                      return (
                        notification.type ===
                          NOTIFICATION_TYPE.SHIPMENT_QUOTE &&
                        notification.source.includes(
                          NOTIFICATION_SOURCE.FOLLOWING
                        )
                      );
                    })
                  }
                  disabled={isUpdating}
                  onChange={() =>
                    handleChange(
                      NOTIFICATION_TYPE.SHIPMENT_QUOTE,
                      NOTIFICATION_SOURCE.FOLLOWING
                    )
                  }
                />
                <label
                  className="custom-control-label"
                  htmlFor="shipment-quotes-following"
                />
              </div>
            </TableData>
          </TableRow>
        </TableBody>
      </Table>
    </Card>
  );
}
